import { useCallback, useEffect, useRef, useState } from 'react'
import type { RealtimeChannel } from '@supabase/supabase-js'
import { APP_SETTINGS } from '../config/appSettings'
import type { Note } from '../domain/note'
import { resolveLinkMetadataApiBaseUrl } from '../links/linkMetadata'
import { getSupabaseClient } from '../supabase/client'
import { getSupabaseConfig } from '../supabase/config'
import { getNativePlatform } from '../utils/nativeRuntime'
import { noteExportSnapshot, type NoteExportOptions } from './contract'

export type NoteExportJobState =
  | 'queued' | 'waiting_for_images' | 'preparing' | 'finalizing'
  | 'ready' | 'failed' | 'canceling' | 'canceled' | 'expired'

export interface NoteExportJob {
  archive_bytes: number | null
  completed_images: number
  completed_notes: number
  created_at: string
  error_code: string | null
  expires_at: string
  filename: string
  format: 'markdown' | 'json'
  include_images: boolean
  include_tags: boolean
  job_id: string
  note_count: number
  phase: string
  ready_at: string | null
  state: NoteExportJobState
  total_images: number
  updated_at: string
}

function apiBaseUrl(): string {
  return resolveLinkMetadataApiBaseUrl({
    configuredBaseUrl: import.meta.env.VITE_API_BASE_URL,
    appEnvironment: getSupabaseConfig()?.environment ?? 'development',
    nativePlatform: getNativePlatform(window),
  })
}

async function authorizedRequest(path: string, init?: RequestInit): Promise<Response> {
  const client = await getSupabaseClient()
  const { data, error } = await client.auth.getSession()
  if (error || !data.session?.access_token) throw new Error('Sign in to prepare an export.')
  const headers = new Headers(init?.headers)
  headers.set('Authorization', `Bearer ${data.session.access_token}`)
  const response = await fetch(`${apiBaseUrl()}${path}`, { ...init, headers })
  if (!response.ok) {
    const body = await response.json().catch(() => null) as { error?: unknown } | null
    throw new Error(typeof body?.error === 'string' ? body.error : `export-request-${response.status}`)
  }
  if (!response.headers.get('Content-Type')?.includes('application/json')) {
    throw new Error('Export service is not available in this environment.')
  }
  return response
}

export async function queueNoteExport(
  notes: readonly Note[],
  options: NoteExportOptions,
): Promise<{ expiresAt: string; jobId: string }> {
  if (notes.length < 1 || notes.length > APP_SETTINGS.exports.notes.maxNoteCount) {
    throw new Error(`Select between 1 and ${APP_SETTINGS.exports.notes.maxNoteCount} notes.`)
  }
  return authorizedRequest('/api/exports', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ notes: notes.map(noteExportSnapshot), options }),
  }).then((response) => response.json() as Promise<{ expiresAt: string; jobId: string }>)
}

export async function downloadNoteExport(jobId: string): Promise<void> {
  const capability = await authorizedRequest(`/api/exports/${jobId}/download-capability`, {
    method: 'POST',
  }).then((response) => response.json() as Promise<{ url: string }>)
  if (getNativePlatform(window)) {
    const { Browser } = await import('@capacitor/browser')
    await Browser.open({ url: capability.url })
    return
  }
  const anchor = document.createElement('a')
  anchor.href = capability.url
  anchor.rel = 'noreferrer'
  anchor.click()
}

export interface NoteExportController {
  attentionJob: NoteExportJob | null
  attentionJobId: string | null
  cancel(jobId: string): Promise<void>
  dismissAttention(): void
  download(jobId: string): Promise<void>
  error: string | null
  jobs: NoteExportJob[]
  loaded: boolean
  loading: boolean
  queue(notes: readonly Note[], options: NoteExportOptions): Promise<string>
  refresh(): Promise<void>
}

const ATTENTION_STORAGE_PREFIX = 'minimany:note-export-attention:v1'

function attentionStorageKey(userId: string): string {
  return `${ATTENTION_STORAGE_PREFIX}:${userId}`
}

function readAttentionJobId(userId: string | null): string | null {
  if (!userId) return null
  try {
    return localStorage.getItem(attentionStorageKey(userId))
  } catch {
    return null
  }
}

function writeAttentionJobId(userId: string | null, jobId: string | null): void {
  if (!userId) return
  try {
    if (jobId) localStorage.setItem(attentionStorageKey(userId), jobId)
    else localStorage.removeItem(attentionStorageKey(userId))
  } catch {
    // Private or constrained storage must not block an export.
  }
}

export function useNoteExports(userId: string | null): NoteExportController {
  const enabled = Boolean(userId)
  const [jobs, setJobs] = useState<NoteExportJob[]>([])
  const [attentionJobId, setAttentionJobId] = useState(() => readAttentionJobId(userId))
  const [loaded, setLoaded] = useState(false)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const inFlightRef = useRef<Promise<void> | null>(null)
  const hasActiveJobsRef = useRef(false)
  const retryDelayRef = useRef(0)
  const retryAtRef = useRef(0)
  const refresh = useCallback(async () => {
    if (!enabled) {
      setJobs([])
      setLoaded(false)
      setLoading(false)
      retryDelayRef.current = 0
      retryAtRef.current = 0
      return
    }
    if (!navigator.onLine || Date.now() < retryAtRef.current) return
    if (inFlightRef.current) return inFlightRef.current
    const request = (async () => {
      setLoading(true)
      try {
        const response = await authorizedRequest('/api/exports')
        const body = await response.json() as { jobs: NoteExportJob[] }
        setJobs(body.jobs)
        setLoaded(true)
        setError(null)
        retryDelayRef.current = 0
        retryAtRef.current = 0
      } catch (requestError) {
        retryDelayRef.current = Math.min(
          APP_SETTINGS.exports.notes.jobPollRetryMaxMs,
          retryDelayRef.current === 0
            ? APP_SETTINGS.exports.notes.jobFallbackPollMs
            : retryDelayRef.current * 2,
        )
        retryAtRef.current = Date.now() + retryDelayRef.current
        setError(requestError instanceof Error ? requestError.message : 'exports-unavailable')
      } finally {
        setLoading(false)
        inFlightRef.current = null
      }
    })()
    inFlightRef.current = request
    return request
  }, [enabled])

  const queue = useCallback(async (notes: readonly Note[], options: NoteExportOptions) => {
    const result = await queueNoteExport(notes, options)
    setAttentionJobId(result.jobId)
    writeAttentionJobId(userId, result.jobId)
    retryAtRef.current = 0
    await refresh()
    return result.jobId
  }, [refresh, userId])

  const dismissAttention = useCallback(() => {
    setAttentionJobId(null)
    writeAttentionJobId(userId, null)
  }, [userId])

  const cancel = useCallback(async (jobId: string) => {
    await authorizedRequest(`/api/exports/${jobId}/cancel`, { method: 'POST' })
    retryAtRef.current = 0
    await refresh()
  }, [refresh])

  const download = useCallback(async (jobId: string) => {
    await downloadNoteExport(jobId)
    if (jobId === attentionJobId) dismissAttention()
  }, [attentionJobId, dismissAttention])

  useEffect(() => {
    setAttentionJobId(readAttentionJobId(userId))
  }, [userId])

  useEffect(() => {
    hasActiveJobsRef.current = jobs.some((job) => (
      job.state === 'queued'
      || job.state === 'waiting_for_images'
      || job.state === 'preparing'
      || job.state === 'finalizing'
      || job.state === 'canceling'
    ))
  }, [jobs])

  useEffect(() => {
    if (!enabled) {
      setJobs([])
      setLoaded(false)
      setLoading(false)
      return
    }
    void refresh()
    const catchUp = () => {
      if (navigator.onLine && document.visibilityState === 'visible') void refresh()
    }
    const reconnect = () => {
      retryAtRef.current = 0
      catchUp()
    }
    window.addEventListener('online', reconnect)
    window.addEventListener('focus', catchUp)
    window.addEventListener('pageshow', catchUp)
    document.addEventListener('visibilitychange', catchUp)
    const interval = window.setInterval(() => {
      if (hasActiveJobsRef.current) catchUp()
    }, APP_SETTINGS.exports.notes.jobFallbackPollMs)
    let disposed = false
    let channel: RealtimeChannel | null = null
    void getSupabaseClient().then(async (client) => {
      if (disposed) return
      await client.realtime.setAuth()
      const config = getSupabaseConfig()
      return client.auth.getUser().then(({ data }) => {
        if (disposed || !data.user) return
        channel = client.channel(
          `minimany-export:${config?.environment ?? 'development'}:${data.user.id}`,
          { config: { private: true } },
        )
        channel.on('broadcast', { event: 'updated' }, () => { void refresh() }).subscribe()
      })
    }).catch(() => undefined)
    return () => {
      disposed = true
      window.clearInterval(interval)
      window.removeEventListener('online', reconnect)
      window.removeEventListener('focus', catchUp)
      window.removeEventListener('pageshow', catchUp)
      document.removeEventListener('visibilitychange', catchUp)
      if (channel) void getSupabaseClient().then((client) => client.removeChannel(channel!))
    }
  }, [enabled, refresh])

  const attentionJob = attentionJobId
    ? jobs.find((job) => job.job_id === attentionJobId) ?? null
    : null

  return {
    attentionJob,
    attentionJobId,
    cancel,
    dismissAttention,
    download,
    error,
    jobs,
    loaded,
    loading,
    queue,
    refresh,
  }
}
